import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

interface NavChild {
    label: string;
    path: string;
    roles: string[];
    // Hide this item for subcontractors even if their roles would otherwise
    // qualify (e.g. Commission Hours: open to "consultant", but
    // subcontractors are explicitly excluded regardless of role).
    excludeIfSubcontractor?: boolean;
}

interface NavGroup {
    label: string;
    icon: string;
    children: NavChild[];
}

const navGroups: NavGroup[] = [
    {
        label: "Home",
        icon: "🏠",
        children: [
            { label: "Home", path: "/", roles: ["consultant", "project_manager", "report_viewer", "hr_payroll", "admin"] },
        ],
    },
    {
        label: "Timesheets",
        icon: "⏱",
        children: [
            { label: "My Timesheets",  path: "/timesheets", roles: ["consultant", "project_manager", "report_viewer", "hr_payroll", "admin"] },
            { label: "Timesheet Templates", path: "/timesheets/templates", roles: ["consultant", "project_manager", "report_viewer", "hr_payroll", "admin"] },
            { label: "Approval Queue", path: "/approvals",  roles: ["project_manager", "admin"] },
            { label: "Timesheet Validation", path: "/timesheets/validation", roles: ["admin"] },
        ],
    },
    {
        label: "Reports",
        icon: "📊",
        children: [
            { label: "Project Financials", path: "/reports/financials",  roles: ["report_viewer", "admin"] },
            { label: "Project Info",       path: "/reports/project-info", roles: ["project_manager", "admin"] },
            {
                label: "Commission Hours", path: "/reports/commission",
                roles: ["consultant", "hr_payroll", "admin"],
                excludeIfSubcontractor: true,
            },
            { label: "Data Quality Report", path: "/reports/data-quality", roles: ["admin"] },
        ],
    },
    {
        label: "Invoicing",
        icon: "🧾",
        children: [
            // 2026-08-28, T&M Invoice Automation build: "Invoicer OR Admin"
            // access boundary, finalized in BACKLOG.md 2026-08-27.
            { label: "T&M Invoice Automation", path: "/invoicing", roles: ["invoicer", "admin"] },
        ],
    },
    {
        label: "Admin",
        icon: "⚙️",
        children: [
            { label: "User Management", path: "/admin/users",     roles: ["admin"] },
            { label: "Zoho Sync",       path: "/admin/zoho-sync", roles: ["admin", "project_manager"] },  // 2026-08-26, backlog #13
            { label: "System Config",   path: "/admin/config",    roles: ["admin"] },
        ],
    },
];

interface SidebarProps {
    roles: string[];
    isSubcontractor?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ roles, isSubcontractor = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { instance } = useMsal();
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

    const hasRole = (required: string[]) =>
        required.some(r => roles.includes(r));

    const visibleGroups = navGroups
        .map(group => ({
            ...group,
            children: group.children.filter(c =>
                hasRole(c.roles) && !(c.excludeIfSubcontractor && isSubcontractor)
            ),
        }))
        .filter(group => group.children.length > 0);

    const isGroupExpanded = (group: NavGroup) => {
        if (collapsed[group.label] !== undefined) return !collapsed[group.label];
        return group.children.some(c => c.path === location.pathname);
    };

    // Flip off the group's actual current expanded state (which may come from
    // the "active child" fallback, not from a stored flag) rather than
    // blindly negating the stored flag -- otherwise the first click after
    // load can land on the same effective state as before and look like it
    // did nothing, requiring a second click to actually open.
    const toggleGroup = (group: NavGroup) => {
        const currentlyExpanded = isGroupExpanded(group);
        setCollapsed(prev => ({ ...prev, [group.label]: currentlyExpanded }));
    };

    const handleLogout = () => instance.logoutRedirect();

    return (
        <div style={{
            width: "220px", minHeight: "100vh", backgroundColor: "#5C5D5E",
            display: "flex", flexDirection: "column", position: "fixed", left: 0, top: 0,
        }}>
            <div style={{ padding: "24px 16px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                <h2 style={{ color: "#F18E00", margin: 0, fontSize: "16px", fontWeight: "bold" }}>Amplify Projects & Financials</h2>
            </div>

            <nav style={{ flex: 1, padding: "8px 0" }}>
                {visibleGroups.map(group => {
                    const expanded = isGroupExpanded(group);
                    const isSingleChild = group.children.length === 1 && group.label === "Home";

                    if (isSingleChild) {
                        const child = group.children[0];
                        const isActive = location.pathname === child.path;
                        return (
                            <div key={group.label} onClick={() => navigate(child.path)} style={{
                                display: "flex", alignItems: "center", padding: "10px 16px",
                                cursor: "pointer",
                                backgroundColor: isActive ? "rgba(241,142,0,0.2)" : "transparent",
                                borderLeft: isActive ? "3px solid #F18E00" : "3px solid transparent",
                                color: isActive ? "#F18E00" : "rgba(255,255,255,0.8)", fontSize: "14px",
                            }}>
                                <span style={{ marginRight: "10px" }}>{group.icon}</span>
                                {group.label}
                            </div>
                        );
                    }

                    return (
                        <div key={group.label}>
                            <div onClick={() => toggleGroup(group)} style={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                padding: "10px 16px", cursor: "pointer",
                                color: "rgba(255,255,255,0.9)", fontSize: "14px", fontWeight: "bold",
                            }}>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <span style={{ marginRight: "10px" }}>{group.icon}</span>
                                    {group.label}
                                </div>
                                <span style={{ fontSize: "10px" }}>{expanded ? "▼" : "▶"}</span>
                            </div>
                            {expanded && group.children.map(child => {
                                const isActive = location.pathname === child.path;
                                return (
                                    <div key={child.path} onClick={() => navigate(child.path)} style={{
                                        display: "flex", alignItems: "center",
                                        padding: "8px 16px 8px 40px", cursor: "pointer",
                                        backgroundColor: isActive ? "rgba(241,142,0,0.2)" : "transparent",
                                        borderLeft: isActive ? "3px solid #F18E00" : "3px solid transparent",
                                        color: isActive ? "#F18E00" : "rgba(255,255,255,0.7)",
                                        fontSize: "13px",
                                    }}>
                                        {child.label}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </nav>

            <div style={{ padding: "16px", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                <div onClick={handleLogout} style={{
                    display: "flex", alignItems: "center", padding: "10px 16px",
                    cursor: "pointer", color: "rgba(255,255,255,0.6)", fontSize: "14px", borderRadius: "4px",
                }}>
                    <span style={{ marginRight: "10px" }}>🚪</span>
                    Sign Out
                </div>
            </div>
        </div>
    );
};

export default Sidebar;